import React, { useEffect, useCallback } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useFormContext, Controller } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import {
	HookFormInputGroup,
	HookFormPhoneInput,
	RequiredMark,
	Skeleton,
} from "components";
import Select from "components/select";
import { UserProfileFields, UserProfilePage } from "globalConstants";
import { useLoader } from "hooks";
import { dropdownValuesActions } from "store";
import { getCompanies } from "store/common/dropdownValues/dropdownValues.reducer";
import {
	alphaNumericNormalizer,
	trimMidSentenceNormalizer,
	trimNormalizer,
} from "validators";
import {
	matchesAddressRegex,
	matchesCityRegex,
	matchesNameRegex,
	zipField,
} from "validators/userValidators";
import {
	dateOfBirth1900,
	dateOfBirthMoreThan18,
	dateValidation,
	maxLength2,
	maxLength30,
	maxLength50,
	maxLength9,
	minLength2,
	minLength5,
	onlyDate,
	onlyLetters,
	onlyTwoUpperCaseLetters,
	required,
} from "validators/validation";

type GeneralInfoFieldsProps = {
	setFullName?: any;
	isLoading?: boolean;
	isEditPage?: boolean;
};

const GeneralInfoFields = ({
	setFullName,
	isLoading = false,
}: GeneralInfoFieldsProps) => {
	const {
		FirstName,
		LastName,
		DateOfBirth,
		Phone,
		MobilePhone,
		Address,
		City,
		State,
		Zip,
		Country,
	} = UserProfileFields;

	const { pathname }: { pathname: string } = useLocation();
	const isUserProfilePage: boolean = pathname.includes(UserProfilePage.name);

	const dispatch = useDispatch();
	const Loader = useLoader(isLoading);
	const companies = useSelector(getCompanies);

	useEffect(() => {
		dispatch(dropdownValuesActions.getCompanies());
	}, [dispatch]);

	const {
		register,
		control,
		setValue,
		watch,
		formState: { errors },
	} = useFormContext();

	const ErrorMessage = useCallback(
		({ field }) => {
			return errors[field] ? (
				<div className="errorMessage">{errors[field]?.message}</div>
			) : null;
		},
		[errors],
	);

	const handleAgentOfficeSelectChange = useCallback(
		agentOffice => {
			setValue("agentOffice", agentOffice, { shouldDirty: true });
			return agentOffice;
		},
		[setValue],
	);

	let sortCompanies = companies.sort((a, b) => {
		let textA = a.name.toUpperCase();
		let textB = b.name.toUpperCase();

		return textA.localeCompare(textB);
	});

	let agentOfficeData = sortCompanies.map(item => {
		const inactiveStatus =
			item?.status?.value === 1 ||
			item?.status?.value === 2 ||
			item?.status?.value === 4;
		if (inactiveStatus) {
			return { ...item, name: `${item.name} (Inactive)` };
		} else {
			return { ...item };
		}
	});

	const firstNameValue = watch(FirstName.name);
	const lastNameValue = watch(LastName.name);

	useEffect(() => {
		setFullName?.(`${firstNameValue} ${lastNameValue}`);
	}, [firstNameValue, lastNameValue, setFullName]);

	return (
		<>
			<Row>
				<Col lg={5}>
					<HookFormInputGroup
						name={FirstName.name}
						register={register}
						validator={{
							required,
							minLength2,
							maxLength30,
							matchesNameRegex,
						}}
						colLabel={5}
						colInput={7}
						label={FirstName.displayName}
						errors={errors}
						normalizeOnBlur={trimNormalizer}
						normalize={trimMidSentenceNormalizer}
						blurLoading={isLoading}
						showRequiredMark
					/>
				</Col>
				<Col
					lg={{
						span: 5,
						offset: 2,
					}}
				>
					<HookFormInputGroup
						name={LastName.name}
						register={register}
						validator={{
							required,
							minLength2,
							maxLength30,
							matchesNameRegex,
						}}
						colLabel={5}
						colInput={7}
						label={LastName.displayName}
						errors={errors}
						normalizeOnBlur={trimNormalizer}
						normalize={trimMidSentenceNormalizer}
						blurLoading={isLoading}
						showRequiredMark
					/>
				</Col>
			</Row>
			<Row>
				{!isUserProfilePage && (
					<Col lg={5}>
						<Row>
							<Col sm={5}>
								<Form.Label htmlFor="agentOffice">
									Agent Office
									<RequiredMark />
								</Form.Label>
							</Col>
							<Col sm={7}>
								<Loader
									skeleton={
										<Skeleton width="100%" height={34} />
									}
									component={
										<Controller
											control={control}
											name="agentOffice"
											rules={{
												validate: { required },
											}}
											defaultValue=""
											render={({ field }) => (
												<Select
													{...field}
													options={agentOfficeData}
													getOptionLabel={({
														name,
													}) => name}
													getOptionValue={({ id }) =>
														id
													}
													placeholder="Please select"
													onChange={
														handleAgentOfficeSelectChange
													}
													hasError={
														!!errors?.agentOffice
													}
												/>
											)}
										/>
									}
								/>

								<ErrorMessage field="agentOffice" />
							</Col>
						</Row>
					</Col>
				)}

				<Col
					lg={{
						span: 5,
						offset: isUserProfilePage ? 0 : 2,
					}}
				>
					<HookFormInputGroup
						name={DateOfBirth.name}
						register={register}
						validator={{
							dateValidation,
							dateOfBirth1900,
							dateOfBirthMoreThan18,
						}}
						colLabel={5}
						colInput={7}
						label={DateOfBirth.displayName}
						errors={errors}
						normalizeOnBlur={trimNormalizer}
						normalize={onlyDate}
						blurLoading={isLoading}
						placeholder="MM/DD/YYYY"
					/>
				</Col>
			</Row>
			<Row>
				<Col lg={5}>
					<HookFormPhoneInput
						name={Phone.name}
						label={Phone.displayName}
						isLoading={isLoading}
						colLabel={5}
						colInput={7}
					/>
				</Col>
				<Col
					lg={{
						span: 5,
						offset: 2,
					}}
				>
					<HookFormPhoneInput
						name={MobilePhone.name}
						label={MobilePhone.displayName}
						isLoading={isLoading}
						colLabel={5}
						colInput={7}
					/>
				</Col>
			</Row>

			<Row>
				<Col lg={5}>
					<HookFormInputGroup
						name={Address.name}
						register={register}
						validator={{
							minLength2,
							maxLength50,
							matchesAddressRegex,
						}}
						colLabel={5}
						colInput={7}
						label={Address.displayName}
						errors={errors}
						normalizeOnBlur={trimNormalizer}
						normalize={trimMidSentenceNormalizer}
						blurLoading={isLoading}
					/>
				</Col>
				<Col
					lg={{
						span: 5,
						offset: 2,
					}}
				>
					<HookFormInputGroup
						name={City.name}
						register={register}
						validator={{
							minLength2,
							maxLength30,
							matchesCityRegex,
						}}
						colLabel={5}
						colInput={7}
						label={City.displayName}
						errors={errors}
						normalizeOnBlur={trimNormalizer}
						normalize={trimMidSentenceNormalizer}
						blurLoading={isLoading}
					/>
				</Col>
			</Row>
			<Row>
				<Col lg={5}>
					<HookFormInputGroup
						name={State.name}
						register={register}
						validator={{
							minLength2,
							maxLength2,
						}}
						colLabel={5}
						colInput={7}
						label={State.displayName}
						errors={errors}
						normalizeOnBlur={trimNormalizer}
						normalize={onlyTwoUpperCaseLetters}
						blurLoading={isLoading}
					/>
				</Col>
				<Col
					lg={{
						span: 5,
						offset: 2,
					}}
				>
					<HookFormInputGroup
						name={Zip.name}
						register={register}
						validator={{
							minLength5,
							maxLength9,
							zipField,
						}}
						colLabel={5}
						colInput={7}
						label={Zip.displayName}
						errors={errors}
						normalizeOnBlur={trimNormalizer}
						normalize={alphaNumericNormalizer}
						blurLoading={isLoading}
					/>
				</Col>
			</Row>
			<Row>
				<Col lg={5}>
					<HookFormInputGroup
						name={Country.name}
						register={register}
						validator={{ maxLength30 }}
						colLabel={5}
						colInput={7}
						label={Country.displayName}
						errors={errors}
						normalizeOnBlur={trimNormalizer}
						normalize={onlyLetters}
						blurLoading={isLoading}
					/>
				</Col>
			</Row>
		</>
	);
};

export default GeneralInfoFields;
